import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { DialogService } from 'primeng/dynamicdialog';
import { ModalLoadingComponent } from '../../../shared/modals/modal-loading/modal-loading.component';
import { ModalRejectComponent } from '../../../shared/modals/modal-reject/modal-reject.component';
import { ModalSuccessComponent } from '../../../shared/modals/modal-success/modal-success.component';
import { EmployeeLocalService } from '../../services/employee.service';

@Component({
  selector: 'app-employee-bulk-load',
  templateUrl: './employee-bulk-load.component.html',
  styleUrls: ['./employee-bulk-load.component.scss'],
  providers: [DialogService],
})
export class EmployeeBulkLoadComponent implements OnChanges {
  @Input() visible: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() registerComplete = new EventEmitter<void>();

  @ViewChild('excelUploader') excelUploader: any;
  @ViewChild('bulkTableAnchor') bulkTableAnchor?: ElementRef<HTMLElement>;

  userData: any = null;
  currentStep: number = 1;
  currentFiles: File[] = [];
  selectedFileNames: string[] = [];
  batchId: string | null = null;
  uploadProgress: number = 0;
  uploadProgressByFile: number[] = [];
  isUploadCompletingByFile: boolean[] = [];
  isDragOver: boolean = false;
  isUploadCompleting: boolean = false;
  private uploadProgressTimers: ReturnType<typeof setInterval>[] = [];
  private uploadCompleteTimers: ReturnType<typeof setTimeout>[] = [];
  private tableLoadingTimer: ReturnType<typeof setTimeout> | null = null;

  dataSource: any[] = [];
  isLoading: boolean = false;
  pageIndex: number = 0;
  pageSize: number = 10;
  totalElements: number = 0;
  metadata: Record<string, unknown> = {};
  companiesMetadata: any[] = [];
  showObservationsModal: boolean = false;
  selectedObservationMessages: string[] = [];
  selectedObservationContext: string = '';
  removingRowId: string | null = null;

  displayedColumns = [
    { key: 'displayRowNumber', header: 'Fila' },
    { key: 'companyName', header: 'Empresa' },
    { key: 'fileName', header: 'Archivo' },
    { key: 'sheetName', header: 'Hoja' },
    { key: 'employee.employeeDocumentNumber', header: 'DNI' },
    { key: 'employee.names', header: 'Nombres' },
    { key: 'employee.lastNames', header: 'Apellidos' },
    { key: 'employee.email', header: 'Correo' },
    { key: 'employee.startDate', header: 'Fecha de Ingreso' },
    { key: 'contract.jobPositionName', header: 'Cargo' },
    { key: 'contract.totalSalary', header: 'Salario' },
    { key: 'status', header: 'Estado' },
    { key: 'messages', header: 'Obs.' },
    { key: 'remove', header: '' },
  ];

  constructor(
    private employeeLocalService: EmployeeLocalService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    const userDataString = localStorage.getItem('access');
    if (userDataString) {
      this.userData = JSON.parse(userDataString);
    }
  }

  ngOnChanges(): void {
    if (this.visible) {
      this.resetState();
    }
  }

  get nextButtonLabel(): string {
    return this.currentStep === 1 ? 'Siguiente' : 'Registrar';
  }

  get hasInvalidRows(): boolean {
    return this.dataSource.some((row) => row.status === 'INVALID');
  }

  get isFileReady(): boolean {
    return (
      this.currentFiles.length > 0 &&
      this.uploadProgressByFile.every((progress) => progress >= 100) &&
      !this.isUploadCompletingByFile.some(Boolean)
    );
  }

  get hasUploadingFile(): boolean {
    return (
      this.currentFiles.length > 0 &&
      (this.uploadProgressByFile.some((progress) => progress < 100) ||
        this.isUploadCompletingByFile.some(Boolean))
    );
  }

  get hasCompletedFile(): boolean {
    return (
      this.currentFiles.length > 0 &&
      this.currentFiles.some((_, index) => this.isFileUploadComplete(index))
    );
  }

  get completedFiles(): { file: File; index: number }[] {
    return this.currentFiles
      .map((file, index) => ({ file, index }))
      .filter(({ index }) => this.isFileUploadComplete(index));
  }

  get uploadingFiles(): { file: File; index: number }[] {
    return this.currentFiles
      .map((file, index) => ({ file, index }))
      .filter(({ index }) => !this.isFileUploadComplete(index));
  }

  get dialogStyle(): Record<string, string> {
    return this.currentStep === 1
      ? { width: '560px', maxWidth: '92vw' }
      : { width: '88vw', maxWidth: '96vw' };
  }

  get dialogStyleClass(): string {
    return [
      'employee-bulk-dialog',
      this.currentStep === 2
        ? 'employee-bulk-dialog-results'
        : 'employee-bulk-dialog-upload',
    ].join(' ');
  }

  openFileUpload(): void {
    this.excelUploader?.choose();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
    event.dataTransfer!.dropEffect = 'copy';
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files
      ? Array.from(event.dataTransfer.files)
      : [];
    this.setFiles(files);
  }

  onChangeFile(event: any): void {
    this.setFiles(Array.from<File>(event.files));
  }

  removeFile(index: number, event?: Event): void {
    event?.stopPropagation();
    const nextProgress = this.uploadProgressByFile.filter(
      (_, fileIndex) => fileIndex !== index
    );
    const nextCompletingState = this.isUploadCompletingByFile.filter(
      (_, fileIndex) => fileIndex !== index
    );

    this.stopAllUploadAnimations();
    this.currentFiles.splice(index, 1);
    this.selectedFileNames.splice(index, 1);

    if (this.currentFiles.length === 0) {
      this.uploadProgressByFile = [];
      this.isUploadCompletingByFile = [];
      this.uploadProgress = 0;
      this.isUploadCompleting = false;
      return;
    }

    this.uploadProgressByFile = nextProgress;
    this.isUploadCompletingByFile = nextCompletingState.map(() => false);
    this.currentFiles.forEach((file, fileIndex) => {
      if ((this.uploadProgressByFile[fileIndex] || 0) < 100) {
        this.startUploadAnimation(file, fileIndex);
      }
    });
    this.syncUploadState();
  }

  private setFiles(files: File[]): void {
    const validFiles = files.filter((file) => this.isValidFile(file));

    if (validFiles.length !== files.length) {
      this.dialogService.open(ModalRejectComponent, {
        header: '',
        data: {
          text: 'Solo se permiten archivos .csv, .xlsx o .xls.',
          title: 'Archivo no valido',
          showButton: true,
          buttonText: 'Entendido',
        },
      });
    }

    const incomingFiles = validFiles.filter(
      (file) => !this.hasFileAlreadySelected(file)
    );

    if (incomingFiles.length === 0) {
      return;
    }

    const startIndex = this.currentFiles.length;
    this.currentFiles = [...this.currentFiles, ...incomingFiles];
    this.selectedFileNames = this.currentFiles.map((file) => file.name);
    this.uploadProgressByFile = [
      ...this.uploadProgressByFile,
      ...incomingFiles.map(() => 0),
    ];
    this.isUploadCompletingByFile = [
      ...this.isUploadCompletingByFile,
      ...incomingFiles.map(() => false),
    ];
    this.batchId = null;
    this.dataSource = [];
    this.totalElements = 0;

    if (incomingFiles.length > 0) {
      incomingFiles.forEach((file, index) =>
        this.startUploadAnimation(file, startIndex + index)
      );
    } else {
      this.uploadProgress = 0;
      this.isUploadCompleting = false;
    }
  }

  private hasFileAlreadySelected(file: File): boolean {
    return this.currentFiles.some(
      (selectedFile) =>
        selectedFile.name === file.name &&
        selectedFile.size === file.size &&
        selectedFile.lastModified === file.lastModified
    );
  }

  private startUploadAnimation(file: File, index: number): void {
    this.stopUploadAnimation(index);
    this.uploadProgressByFile[index] = 0;
    this.isUploadCompletingByFile[index] = false;
    this.syncUploadState();

    const sizeInMb = file.size / 1024 / 1024;
    const durationMs = Math.min(Math.max(sizeInMb * 350, 900), 3600);
    const intervalMs = 80;
    const increment = 100 / (durationMs / intervalMs);

    this.uploadProgressTimers[index] = setInterval(() => {
      const jitter = Math.random() * 2.8;
      this.uploadProgressByFile[index] = Math.min(
        100,
        Math.round((this.uploadProgressByFile[index] || 0) + increment + jitter)
      );
      this.syncUploadState();

      if (this.uploadProgressByFile[index] >= 100) {
        this.uploadProgressByFile[index] = 100;
        this.stopUploadProgressTimer(index);
        this.isUploadCompletingByFile[index] = true;
        this.syncUploadState();
        this.uploadCompleteTimers[index] = setTimeout(() => {
          this.isUploadCompletingByFile[index] = false;
          this.syncUploadState();
        }, 520);
      }
    }, intervalMs);
  }

  private stopUploadProgressTimer(index: number): void {
    if (this.uploadProgressTimers[index]) {
      clearInterval(this.uploadProgressTimers[index]);
      delete this.uploadProgressTimers[index];
    }
  }

  private stopUploadAnimation(index: number): void {
    this.stopUploadProgressTimer(index);

    if (this.uploadCompleteTimers[index]) {
      clearTimeout(this.uploadCompleteTimers[index]);
      delete this.uploadCompleteTimers[index];
    }
  }

  private stopAllUploadAnimations(): void {
    this.uploadProgressTimers.forEach((timer) => timer && clearInterval(timer));
    this.uploadCompleteTimers.forEach((timer) => timer && clearTimeout(timer));
    this.uploadProgressTimers = [];
    this.uploadCompleteTimers = [];
  }

  private syncUploadState(): void {
    const activeProgress = this.uploadProgressByFile.length
      ? Math.min(...this.uploadProgressByFile)
      : 0;
    this.uploadProgress = activeProgress;
    this.isUploadCompleting = this.isUploadCompletingByFile.some(Boolean);
  }

  getUploadProgress(index: number): number {
    return this.uploadProgressByFile[index] || 0;
  }

  isFileUploadComplete(index: number): boolean {
    return (
      (this.uploadProgressByFile[index] || 0) >= 100 &&
      !this.isUploadCompletingByFile[index]
    );
  }

  private isValidFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    return (
      fileName.endsWith('.csv') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls')
    );
  }

  private buildProcessFormData(): FormData {
    const formData = new FormData();

    this.currentFiles.forEach((file) => {
      formData.append('files', file, file.name);
    });

    if (this.currentFiles[0]) {
      formData.append('file', this.currentFiles[0], this.currentFiles[0].name);
    }

    if (this.userData?.employeeId) {
      formData.append('userId', this.userData.employeeId);
    }

    formData.append('page', String(this.pageIndex));
    formData.append('size', String(this.pageSize));

    return formData;
  }

  private applyProcessResponse(response: any): void {
    this.metadata = response.metadata || {};
    this.companiesMetadata = Array.isArray(this.metadata['companies'])
      ? (this.metadata['companies'] as any[])
      : [];
    this.batchId = (this.metadata['batchId'] as string) || this.batchId;
    this.dataSource = response.content || [];
    this.pageIndex = response.page.page;
    this.pageSize = response.page.size;
    this.totalElements = response.page.totalElements;
  }

  private showDeferredTableLoading(delayMs: number = 240): void {
    this.clearDeferredTableLoading();
    this.tableLoadingTimer = setTimeout(() => {
      this.isLoading = true;
    }, delayMs);
  }

  private clearDeferredTableLoading(): void {
    if (this.tableLoadingTimer) {
      clearTimeout(this.tableLoadingTimer);
      this.tableLoadingTimer = null;
    }
  }

  processFile(resetPage: boolean = false): void {
    if (resetPage) {
      this.pageIndex = 0;
    }

    const loadingRef = this.dialogService.open(ModalLoadingComponent, {
      closable: false,
    });
    this.isLoading = true;

    this.employeeLocalService.postEmployeeBulkProcess(this.buildProcessFormData()).subscribe({
      next: (response) => {
        loadingRef.close();
        this.applyProcessResponse(response);
        this.isLoading = false;
        this.currentStep = 2;
      },
      error: (err) => {
        loadingRef.close();
        this.isLoading = false;
        this.showError(this.getHttpErrorMessage(err, 'No se pudo procesar el archivo.'));
      },
    });
  }

  loadProcessedPage(): void {
    if (!this.batchId) {
      this.processFile();
      return;
    }

    this.showDeferredTableLoading();

    this.employeeLocalService
      .postEmployeeBulkProcessPage({
        batchId: this.batchId,
        page: this.pageIndex,
        size: this.pageSize,
      })
      .subscribe({
        next: (response) => {
          this.clearDeferredTableLoading();
          this.applyProcessResponse(response);
          this.isLoading = false;
        },
        error: (err) => {
          this.clearDeferredTableLoading();
          this.isLoading = false;
          this.showError(
            this.getHttpErrorMessage(
              err,
              'No se pudo obtener la pagina solicitada.'
            )
          );
        },
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProcessedPage();
  }

  removePreviewRow(row: any, event?: Event): void {
    event?.stopPropagation();

    if (!this.batchId || !row?.rowId) {
      this.showError('No se encontro la fila para eliminar.');
      return;
    }

    const scrollPosition = this.getTableScrollPosition();
    this.removingRowId = row.rowId;
    this.isLoading = true;

    this.employeeLocalService
      .postEmployeeBulkRemoveRow({
        batchId: this.batchId,
        rowId: row.rowId,
        page: this.pageIndex,
        size: this.pageSize,
      })
      .subscribe({
        next: (response) => {
          this.applyProcessResponse(response);
          this.removingRowId = null;
          this.isLoading = false;
          this.restoreTableScrollPosition(scrollPosition);
        },
        error: (err) => {
          this.removingRowId = null;
          this.isLoading = false;
          this.showError(
            this.getHttpErrorMessage(
              err,
              'No se pudo eliminar la fila seleccionada.'
            )
          );
        },
      });
  }

  private getTableScrollPosition(): { left: number; top: number } {
    const tableContainer = this.getTableContainerElement();

    return {
      left: tableContainer?.scrollLeft || 0,
      top: tableContainer?.scrollTop || 0,
    };
  }

  private restoreTableScrollPosition(position: { left: number; top: number }): void {
    requestAnimationFrame(() => {
      const tableContainer = this.getTableContainerElement();
      if (!tableContainer) return;

      tableContainer.scrollLeft = position.left;
      tableContainer.scrollTop = position.top;
    });
  }

  private getTableContainerElement(): HTMLElement | null {
    return (
      this.bulkTableAnchor?.nativeElement.querySelector('.table-container') ||
      null
    );
  }

  nextStep(): void {
    if (this.currentStep === 1) {
      if (!this.isFileReady) return;
      this.processFile(true);
      return;
    }

    this.registerEmployees();
  }

  registerEmployees(): void {
    if (!this.batchId) {
      this.showError('No se encontro el lote procesado.');
      return;
    }

    const loadingRef = this.dialogService.open(ModalLoadingComponent, {
      closable: false,
    });

    this.employeeLocalService
      .postEmployeeBulkRegister({
        batchId: this.batchId,
        userId: this.userData?.employeeId,
      })
      .subscribe({
        next: (response) => {
          loadingRef.close();
          this.dialogService.open(ModalSuccessComponent, {
            header: '',
            data: {
              text:
                response?.message ||
                'La carga masiva de empleados se registro correctamente.',
              title: 'Carga registrada',
              icon: 'pi pi-check-circle',
              showButton: true,
              buttonText: 'Entendido',
            },
          });
          this.registerComplete.emit();
          this.closeDialog();
        },
        error: (err) => {
          loadingRef.close();
          this.showError(
            this.getHttpErrorMessage(
              err,
              'Ocurrio un error al registrar la carga masiva.'
            )
          );
        },
      });
  }

  backStep(): void {
    if (this.currentStep === 1) {
      this.closeDialog();
      return;
    }

    this.currentStep = 1;
  }

  goBack(): void {
    this.closeDialog();
  }

  closeDialog(): void {
    this.clearDeferredTableLoading();
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  private resetState(): void {
    this.clearDeferredTableLoading();
    this.stopAllUploadAnimations();
    this.currentStep = 1;
    this.currentFiles = [];
    this.selectedFileNames = [];
    this.uploadProgress = 0;
    this.uploadProgressByFile = [];
    this.isUploadCompletingByFile = [];
    this.isUploadCompleting = false;
    this.batchId = null;
    this.dataSource = [];
    this.isLoading = false;
    this.pageIndex = 0;
    this.pageSize = 10;
    this.totalElements = 0;
    this.metadata = {};
    this.companiesMetadata = [];
    this.showObservationsModal = false;
    this.selectedObservationMessages = [];
    this.selectedObservationContext = '';
    this.isDragOver = false;
  }

  getRowMessages(row: any): string {
    return this.getRowMessagesList(row).join(', ');
  }

  getRowMessagesList(row: any): string[] {
    const errors = Array.isArray(row.errors) ? row.errors : [];
    const warnings = Array.isArray(row.warnings) ? row.warnings : [];
    return [...errors, ...warnings].filter(
      (message) => typeof message === 'string' && message.trim().length > 0
    );
  }

  hasRowMessages(row: any): boolean {
    return this.getRowMessagesList(row).length > 0;
  }

  openObservationsModal(row: any, event?: Event): void {
    event?.stopPropagation();
    this.selectedObservationMessages = this.getRowMessagesList(row);
    this.selectedObservationContext = this.buildObservationContext(row);
    this.showObservationsModal = true;
  }

  formatDate(value: any): string {
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day] = value;
      return `${String(day).padStart(2, '0')}/${String(month).padStart(
        2,
        '0'
      )}/${year}`;
    }

    if (typeof value === 'string' && value.trim()) {
      const isoDateMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoDateMatch) {
        const [, year, month, day] = isoDateMatch;
        return `${day}/${month}/${year}`;
      }

      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return `${String(date.getDate()).padStart(2, '0')}/${String(
          date.getMonth() + 1
        ).padStart(2, '0')}/${date.getFullYear()}`;
      }
    }

    return '-';
  }

  formatCurrency(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const normalizedValue =
      typeof value === 'string'
        ? value.replace(/[^\d.,-]/g, '').replace(',', '.')
        : value;
    const amount = Number(normalizedValue);

    if (Number.isNaN(amount)) {
      return '-';
    }

    return `S/. ${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  private buildObservationContext(row: any): string {
    const names = [row?.employee?.names, row?.employee?.lastNames]
      .filter(Boolean)
      .join(' ');
    const document = row?.employee?.employeeDocumentNumber;
    const rowNumber = row?.displayRowNumber || row?.rowNumber;
    const rowLabel = rowNumber ? `Fila ${rowNumber}` : '';
    const employeeLabel = names || document || 'Empleado sin identificar';

    return [rowLabel, employeeLabel, document ? `DNI ${document}` : '']
      .filter(Boolean)
      .join(' - ');
  }

  formatFileSize(file: File): string {
    const sizeInMb = file.size / 1024 / 1024;
    if (sizeInMb >= 1) {
      return `${sizeInMb.toFixed(2)} MB`;
    }

    return `${(file.size / 1024).toFixed(0)} KB`;
  }

  private showError(message: string): void {
    this.dialogService.open(ModalRejectComponent, {
      header: '',
      data: {
        text: message,
        title: 'Ups',
        showButton: true,
        buttonText: 'Entendido',
      },
    });
  }

  private getHttpErrorMessage(error: any, fallback: string): string {
    if (typeof error?.error === 'string' && error.error.trim()) {
      return error.error;
    }

    if (typeof error?.error?.message === 'string' && error.error.message.trim()) {
      return error.error.message;
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }

    return fallback;
  }
}
